// Delete Team Modal Component

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AlertTriangle } from 'lucide-react';
import type { Team, User } from '@/types/teams';
import { useToast } from '@/hooks/use-toast';

interface DeleteTeamModalProps {
  readonly team: Team;
  readonly members: User[];
  readonly onClose: () => void;
  readonly onConfirm: () => Promise<void>;
}

export function DeleteTeamModal({
  team,
  members,
  onClose,
  onConfirm,
}: Readonly<DeleteTeamModalProps>) {
  const { toast } = useToast();
  const [confirmText, setConfirmText] = useState('');
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const usersWithOnlyThisTeam = members.filter(m => m.teams.length === 1);

  const handleDelete = async () => {
    if (confirmText !== 'DELETE' || !confirmChecked) {
      toast({
        title: 'Confirmation required',
        description: 'Please complete all confirmation steps',
        variant: 'destructive',
      });
      return;
    }

    setIsDeleting(true);
    try {
      await onConfirm();
    } catch (error) {
      console.error('Failed to delete team:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete team. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const canDelete = confirmText === 'DELETE' && confirmChecked;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-destructive" />
            </div>
            <div>
              <DialogTitle>Delete {team.name}?</DialogTitle>
              <DialogDescription>
                This action cannot be undone
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 -mx-6 px-6">
          <div className="space-y-6 py-4">
            {/* Impact Summary */}
            <div className="space-y-2">
              <h4 className="font-semibold text-sm">Impact Summary</h4>
              <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                <p className="text-sm">
                  This team currently has <strong>{members.length}</strong> {members.length === 1 ? 'member' : 'members'}
                </p>
                {members.length > 0 && (
                  <p className="text-sm">
                    All members will be unassigned from this team
                  </p>
                )}
              </div>
            </div>

            {/* User Reassignment */}
            {members.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-semibold text-sm">Affected Users</h4>
                {members.length <= 10 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader className="bg-muted">
                        <TableRow>
                          <TableHead>User</TableHead>
                          <TableHead>Other Teams</TableHead>
                          <TableHead className="w-12"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody className="bg-white dark:bg-background">
                        {members.map(member => {
                          const otherTeamsCount = member.teams.length - 1;
                          const isOnlyTeam = member.teams.length === 1;
                          
                          return (
                            <TableRow key={member.id}>
                              <TableCell>
                                <div>
                                  <p className="font-medium text-sm">{member.name}</p>
                                  <p className="text-xs text-muted-foreground">{member.email}</p>
                                </div>
                              </TableCell>
                              <TableCell>
                                {isOnlyTeam ? (
                                  <Badge variant="outline">No other teams</Badge>
                                ) : (
                                  <Badge variant="secondary">{otherTeamsCount} other {otherTeamsCount === 1 ? 'team' : 'teams'}</Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                {isOnlyTeam && (
                                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <p className="text-sm">
                      {members.length} users will be affected by this deletion
                    </p>
                  </div>
                )}

                {usersWithOnlyThisTeam.length > 0 && (
                  <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 p-4 rounded-lg">
                    <div className="flex gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                          {usersWithOnlyThisTeam.length} {usersWithOnlyThisTeam.length === 1 ? 'user has' : 'users have'} only this team
                        </p>
                        <p className="text-sm text-amber-800 dark:text-amber-200">
                          These users will be automatically assigned to the Standard Users team
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Confirmation Required */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="font-semibold text-sm">Confirmation Required</h4>
              
              <div className="flex items-start gap-3">
                <Checkbox
                  id="confirm-checkbox"
                  checked={confirmChecked}
                  onCheckedChange={(checked) => setConfirmChecked(checked as boolean)}
                />
                <label
                  htmlFor="confirm-checkbox"
                  className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  I understand that {members.length} {members.length === 1 ? 'member' : 'members'} will be removed from this team
                </label>
              </div>

              <div className="space-y-2">
                <label htmlFor="confirm-text" className="text-sm font-medium">
                  Type <strong>DELETE</strong> to confirm
                </label>
                <Input
                  id="confirm-text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="font-mono"
                />
              </div>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!canDelete || isDeleting}
          >
            {isDeleting ? 'Deleting Team...' : 'Delete Team'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
